/**
 * Settings — grouped cards. Preserves existing API key rotation and the
 * auto/silero/energy VAD picker.
 */
import { useEffect, useState, type ReactNode } from "react";
import * as api from "../api";
import { authStore } from "../store";
import { getApiKey, getBaseUrl, setApiKey, setBaseUrl } from "../config";
import { Btn, IconBtn, Modal, Segmented, Toggle, LangList, LangBadge, langByCode, useLanguages, type Who } from "../ui";
import { Icon } from "../icons";
import type { Go } from "../App";

export function SettingsScreen({
  theme, toggleTheme, go,
}: {
  theme: "light" | "dark"; toggleTheme: () => void; go: Go;
}) {
  const user = authStore((s) => s.user);
  const langs = useLanguages();

  const [displayName, setDisplayName] = useState(user?.display_name ?? "");
  const [ownerLang, setOwnerLang] = useState(user?.owner_lang ?? "en-US");
  const [otherLang, setOtherLang] = useState(user?.other_lang_override ?? user?.fallback_other_lang ?? "hi-IN");
  const [gender, setGender] = useState<"female" | "male">(user?.tts_voice_gender ?? "female");
  const [retentionDays, setRetentionDays] = useState<number>(user?.audio_retention_days ?? 30);

  const [keys, setKeys] = useState<api.ApiKeyOut[]>([]);
  const [usage, setUsage] = useState<api.ApiKeyUsage | null>(null);
  const [mintedKey, setMintedKey] = useState<string | null>(null);

  const [baseUrl, setBaseUrlLocal] = useState(getBaseUrl());
  const [apiKey, setApiKeyLocal] = useState(getApiKey());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [pick, setPick] = useState<null | { who: Who; current: string }>(null);
  const [query, setQuery] = useState("");

  useEffect(() => { reloadKeys(); }, []);

  async function reloadKeys() {
    try {
      const ks = await api.listApiKeys();
      setKeys(ks);
      const active = ks.find((k) => !k.revoked_at);
      if (active) setUsage(await api.getApiKeyUsage(active.id).catch(() => null));
      else setUsage(null);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  async function saveProfile() {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const next = await api.patchMe({
        display_name: displayName,
        owner_lang: ownerLang,
        fallback_other_lang: otherLang,
        other_lang_override: otherLang,
        tts_voice_gender: gender,
        audio_retention_days: retentionDays,
      });
      authStore.getState().setUser(next);
      setMsg("Saved.");
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function rotateKey() {
    setBusy(true); setErr(null);
    try {
      const created = await api.createApiKey("web");
      setMintedKey(created.key);
      await reloadKeys();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  function saveConfig() {
    setBaseUrl(baseUrl);
    setApiKey(apiKey);
    setMsg("Server settings updated. Reload to take effect.");
  }

  async function logout() {
    const t = authStore.getState().tokens;
    if (t) await api.logout(t.refresh_token).catch(() => {});
    authStore.getState().logout();
  }

  async function delAccount() {
    if (!confirm("Permanently delete your account and all conversations? This cannot be undone.")) return;
    try { await api.deleteAccount(); authStore.getState().logout(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  const owner = langByCode(ownerLang, langs);
  const guest = langByCode(otherLang, langs);

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 28px 60px" }}>
      <h1 className="display" style={{ fontSize: 28, fontWeight: 700, color: "var(--ink)", margin: 0, letterSpacing: -0.5 }}>
        Settings
      </h1>

      {err && <Banner kind="bad" onClose={() => setErr(null)}>{err}</Banner>}
      {msg && <Banner kind="good" onClose={() => setMsg(null)}>{msg}</Banner>}

      <SettingsGroup title="Profile">
        <SettingsRow label="Display name">
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={inputCss} />
        </SettingsRow>
        <SettingsDivider />
        <SettingsRow label="Email" hint="Sign-in identifier — contact support to change.">
          <span style={{ color: "var(--ink-3)" }}>{user?.email}</span>
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Languages">
        <SettingsRow label="I speak">
          <LangButton lang={owner} who="you" onClick={() => setPick({ who: "you", current: ownerLang })} />
        </SettingsRow>
        <SettingsDivider />
        <SettingsRow label="Translate to">
          <LangButton lang={guest} who="guest" onClick={() => setPick({ who: "guest", current: otherLang })} />
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Voice & audio">
        <SettingsRow label="Spoken voice">
          <Segmented options={["female", "male"] as const} value={gender} onChange={setGender} />
        </SettingsRow>
        <SettingsDivider />
        <SettingsRow label="Keep audio for" hint={`${retentionDays} day${retentionDays === 1 ? "" : "s"}`}>
          <input type="range" min={1} max={90} value={retentionDays} onChange={(e) => setRetentionDays(Number(e.target.value))}
            style={{ width: 180, accentColor: "var(--accent)" }} />
        </SettingsRow>

      </SettingsGroup>

      <SettingsGroup title="Appearance">
        <SettingsRow label="Dark mode">
          <Toggle on={theme === "dark"} onClick={toggleTheme} />
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Server">
        <SettingsRow label="Base URL" hint="Leave default to use the current host.">
          <input value={baseUrl} onChange={(e) => setBaseUrlLocal(e.target.value)} className="mono" style={{ ...inputCss, width: 280 }} />
        </SettingsRow>
        <SettingsDivider />
        <SettingsRow label="X-API-Key" hint="Stored in this browser only.">
          <input value={apiKey} onChange={(e) => setApiKeyLocal(e.target.value)} className="mono" style={{ ...inputCss, width: 280 }} />
        </SettingsRow>
        <SettingsDivider />
        <div style={{ padding: "12px 0", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="surface" size="sm" onClick={saveConfig}>Save server settings</Btn>
        </div>
      </SettingsGroup>

      <SettingsGroup title="API keys">
        <div style={{ padding: "12px 4px" }}>
          {keys.length === 0 && <div style={{ color: "var(--ink-3)", fontSize: 14 }}>No API keys minted yet.</div>}
          {keys.map((k) => (
            <div key={k.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "8px 0", fontSize: 13.5,
            }}>
              <Icon name="key" size={16} color={k.revoked_at ? "var(--ink-faint)" : "var(--accent)"} />
              <span className="mono" style={{ color: "var(--ink)" }}>#{k.id}</span>
              <span style={{ color: "var(--ink-3)" }}>{k.label}</span>
              {k.revoked_at && <span style={{ color: "var(--ink-faint)" }}>(revoked)</span>}
              <span style={{ flex: 1 }} />
              {!k.revoked_at && (
                <IconBtn name="trash" onClick={async () => { await api.revokeApiKey(k.id); reloadKeys(); }}
                  size={32} ic={15} color="var(--bad)" />
              )}
            </div>
          ))}
          {usage && (
            <div className="mono" style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 8 }}>
              Today: {usage.audio_seconds.toFixed(1)}s · ${usage.cost_usd.toFixed(4)} · {usage.session_count} sessions
            </div>
          )}
          {mintedKey && (
            <div style={{
              marginTop: 10, padding: "10px 12px",
              background: "color-mix(in oklch, var(--good) 10%, transparent)",
              border: "1px solid color-mix(in oklch, var(--good) 35%, transparent)",
              borderRadius: 12, fontSize: 12.5,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: "var(--ink)" }}>New API key (save this — it won't be shown again):</div>
              <code style={{ wordBreak: "break-all" }} className="mono">{mintedKey}</code>
            </div>
          )}
          <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
            <Btn variant="tonal" size="sm" icon="refresh" onClick={rotateKey} disabled={busy}>Mint new key</Btn>
          </div>
        </div>
      </SettingsGroup>

      <div style={{ display: "flex", gap: 12, justifyContent: "space-between", marginTop: 28 }}>
        <Btn variant="outline" icon="logout" onClick={logout}>Log out</Btn>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn variant="outline" onClick={() => go("home")}>Cancel</Btn>
          <Btn onClick={saveProfile} disabled={busy}>Save profile</Btn>
        </div>
      </div>
      <div style={{ marginTop: 26, textAlign: "center" }}>
        <button onClick={delAccount} style={{
          background: "transparent", border: "none", cursor: "pointer", color: "var(--bad)",
          fontFamily: "var(--font-ui)", fontSize: 13.5, fontWeight: 600, padding: 6,
        }}>Delete account</button>
      </div>

      <Modal open={!!pick} onClose={() => { setPick(null); setQuery(""); }}
        title={pick?.who === "you" ? "I speak…" : "Translate to…"}>
        {pick && (
          <LangList activeCode={pick.current} who={pick.who} query={query} setQuery={setQuery} showRecents
            onPick={(code) => {
              if (pick.who === "you") setOwnerLang(code); else setOtherLang(code);
              setPick(null); setQuery("");
            }} />
        )}
      </Modal>
    </div>
  );
}

function SettingsGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginTop: 28 }}>
      <div className="mono" style={{
        fontSize: 11, letterSpacing: 1.4, color: "var(--ink-3)", textTransform: "uppercase",
        marginBottom: 10, paddingLeft: 4,
      }}>{title}</div>
      <div style={{
        background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: "var(--r-lg)", padding: "4px 18px",
      }}>{children}</div>
    </section>
  );
}

function SettingsRow({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "14px 0", gap: 18 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>{label}</div>
        {hint && <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>{children}</div>
    </div>
  );
}

function SettingsDivider() { return <div style={{ height: 1, background: "var(--border)" }} />; }

function Banner({ kind, children, onClose }: { kind: "good" | "bad"; children: ReactNode; onClose: () => void }) {
  const color = kind === "good" ? "var(--good)" : "var(--bad)";
  return (
    <div style={{
      marginTop: 16, background: `color-mix(in oklch, ${color} 12%, transparent)`,
      color, padding: "10px 14px", borderRadius: 12, fontSize: 13.5,
      display: "flex", alignItems: "center", gap: 10,
    }}>
      {children}
      <button onClick={onClose} style={{
        marginLeft: "auto", background: "transparent", border: "none", cursor: "pointer", color, display: "flex", padding: 2,
      }}><Icon name="x" size={14} /></button>
    </div>
  );
}

function LangButton({ lang, who, onClick }: { lang: { code: string; short: string; name: string }; who: Who; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 9, padding: "8px 12px",
      borderRadius: "var(--r-pill)", border: "1px solid var(--border)",
      background: "var(--surface-2)", cursor: "pointer", fontFamily: "var(--font-ui)",
    }}>
      <LangBadge short={lang.short} who={who} size="sm" />
      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{lang.name}</span>
      <Icon name="chevronDown" size={14} color="var(--ink-3)" />
    </button>
  );
}

const inputCss: React.CSSProperties = {
  height: 38, padding: "0 12px", fontSize: 14, color: "var(--ink)",
  background: "var(--surface-2)", border: "1px solid var(--border)",
  borderRadius: 10, fontFamily: "var(--font-ui)", outline: "none", minWidth: 0,
};
